import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { DisbursementVM } from '@/types/disbursements';

export const useDisbursementsStore = defineStore('disbursements', () => {  
  const disbursements = ref<DisbursementVM[]>([]);
 
  const dirtyIds = ref(new Set<number>());
 
  const editingPostDisbRequest = ref<{
    disbursementId: number;
    postDisbursementChangeAmountLedgerId: number;
  } | null>(null);
    
  const justSaved = ref(false);
    
  const hasDirty = computed(() => dirtyIds.value.size > 0);
    
  const disbursedCount = computed(() => 
    disbursements.value.filter(d => d.details.isDisbursed).length
  );
    
  const getDisbursementById = computed(() => {
    return (id: number) => disbursements.value.find(d => d.details.id === id);
  });
 
  const isDisbursementDirty = computed(() => {
    return (id: number) => dirtyIds.value.has(id);
  });

  interface DisbursementFormState {
    disbursementId: number;
    currentAmount: number | null;
  }
  
  const formStates = ref<Map<number, DisbursementFormState>>(new Map());

  function updateFormAmount(disbursementId: number, amount: number | null) {
    formStates.value.set(disbursementId, {
      disbursementId,
      currentAmount: amount,
    });
  }

  function clearFormAmount(disbursementId: number) {
    formStates.value.delete(disbursementId);
  }

  function getFormAmount(disbursementId: number): number | null {
    return formStates.value.get(disbursementId)?.currentAmount ?? null;
  }

  function setDisbursements(data: DisbursementVM[]) {    
    disbursements.value = data ? [...data] : [];
    if (justSaved.value) {
      justSaved.value = false;
    }
  }
  
  function updateDisbursement(id: number, updates: Partial<DisbursementVM>) {
    const index = disbursements.value.findIndex(d => d.details.id === id);
    if (index !== -1) {      
      disbursements.value[index] = {
        ...disbursements.value[index],
        ...updates,
      };    
    }
  }
    
  function markDirty(id: number) {    
    dirtyIds.value.add(id);
  }
    
  function clearDirty(id: number) {    
    dirtyIds.value.delete(id);
  }
  
  
  function clearAllDirty() {    
    dirtyIds.value.clear();
  }
    
  function setEditRequest(request: {
    disbursementId: number;
    postDisbursementChangeAmountLedgerId: number;
  } | null) {    
    editingPostDisbRequest.value = request;
  }
    
  function setJustSaved(value: boolean) {    
    justSaved.value = value;
  }
 
  function $reset() {    
    disbursements.value = [];
    dirtyIds.value.clear();
    editingPostDisbRequest.value = null;
    justSaved.value = false;
  }
    
  return {    
    disbursements,
    dirtyIds,
    editingPostDisbRequest,
    justSaved,
    hasDirty,
    disbursedCount,
    getDisbursementById,
    isDisbursementDirty,
    setDisbursements,
    updateDisbursement,
    markDirty,
    clearDirty,
    clearAllDirty,
    setEditRequest,
    setJustSaved,
    $reset,
    updateFormAmount,
    clearFormAmount,
    getFormAmount
  };
});